import moment from 'moment';
import Honeybadger from '@honeybadger-io/js';

// NOTE: if adding a locale here, also update:
//   config/application.rb (I18n.available_locales)
//   app/assets/javascripts/services/language.js (SUPPORTED_LANGUAGES)

// SUPPORTED_LOCALES is for basic logic checks
export const SUPPORTED_LOCALES = ['en', 'ar', 'es', 'fa', 'fil', 'hi', 'hy', 'ja', 'km', 'ko', 'ru', 'th', 'uk', 'ur', 'vi', 'zh-CN', 'zh-TW'];

// LANGUAGES is for dropdown menus and assessments
export const LANGUAGES = [
  { label: 'English', value: 'en' },
  { label: 'عربي', value: 'ar' },
  { label: 'Español', value: 'es' },
  { label: 'فارسی', value: 'fa' },
  { label: 'Pilipino', value: 'fil' },
  { label: 'हिन्दी', value: 'hi' },
  { label: 'Հայերեն', value: 'hy' },
  { label: '日本語', value: 'ja' },
  { label: 'ភាសាខ្មែរ', value: 'km' },
  { label: '한국어', value: 'ko' },
  { label: 'Русский', value: 'ru' },
  { label: 'ภาษาไทย', value: 'th' },
  { label: 'Українська', value: 'uk' },
  { label: 'اردو', value: 'ur' },
  { label: 'Tiếng Việt', value: 'vi' },
  { label: '简体中文', value: 'zh-CN' },
  { label: '繁體中文', value: 'zh-TW' }
];

// Module-level mirror of the current language, written by LanguageProvider on every render.
// Read by withTranslations and the Language compat shim below.
let _currentLang = 'en';
export const getLanguage = () => _currentLang;
export const setLanguageMirror = (lang) => {
  _currentLang = lang;
};

// Standalone translation utilities (previously class methods)
const interpolateString = (template, vars) =>
  template.replace(/\${(.*?)}/g, (match, p1) => {
    const keys = p1.split('.');
    let value = vars;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return match;
      }
    }
    return value;
  });

const fill_template = (template, vars) => {
  let interpolated;
  try {
    interpolated = interpolateString(template, vars);
  } catch {
    console.warn('Failed translating a dynamic string');
    interpolated = template;
  }
  return interpolated;
};

export const t = (translations, text, vars = null) => {
  const template = translations[text] || text || '';
  if (!vars) return template;
  return fill_template(template, vars);
};

export const getLanguageString = (text, use_locale = null) => {
  const locale = use_locale || getLanguage();
  if (!Array.isArray(text)) return text;
  let langString = null;
  text.forEach((lang) => {
    if (lang.langCode.includes(locale)) langString = lang.text;
  });
  return langString;
};

// useSyncExternalStore integration: re-renders React when any translation file finishes loading.
// snapshotVersion increments each time translations load; React uses it to detect changes.
let rerenderScheduled = false;
const rerenderCallbacks = new Set();
let snapshotVersion = 0;

export const subscribeToTranslationUpdates = (callback) => {
  rerenderCallbacks.add(callback);
  return () => rerenderCallbacks.delete(callback);
};

export const getTranslationsSnapshot = () => snapshotVersion;

export const notifyTranslationsLoaded = () => {
  if (rerenderScheduled) return;
  rerenderScheduled = true;
  Promise.resolve().then(() => {
    rerenderScheduled = false;
    snapshotVersion++;
    rerenderCallbacks.forEach((cb) => cb());
  });
};

export const toMomentLocale = (lang) => {
  if (lang === 'hy') return 'hy-am';
  return lang.toLowerCase();
};

export const loadMomentLocale = (lang) => {
  const momentLang = toMomentLocale(lang);
  if (momentLang === 'en') {
    moment.locale('en');
    notifyTranslationsLoaded();
    return;
  }
  import(`moment/locale/${momentLang}`)
    .then(() => {
      moment.locale(momentLang);
      notifyTranslationsLoaded();
    })
    .catch((err) => Honeybadger.notify(err, { context: { lang, momentLang } }));
};

// withTranslations wraps dynamic JSON imports so each translation file set shares one cache.
// Call it once at module level; it returns a t(text, vars) lookup function.
//
// enDefault: optional synchronously-loaded 'en' translation object. When provided, both SSR and the client's
// first render use real English values instead of raw keys, eliminating hydration mismatches
export const withTranslations = (loader, enDefault = null) => {
  const loadedByLang = {};
  if (enDefault !== null) {
    loadedByLang['en'] = enDefault;
  }

  const ensureLoaded = (lang) => {
    // Never fire async loads on the server — keeps loadedByLang frozen at enDefault (English)
    // for every SSR request, matching the client's always-English first render and avoiding
    // hydration mismatches.
    if (typeof window === 'undefined') return;
    // Already loaded or in-flight — skip redundant loader() calls.
    if (loadedByLang[lang] !== undefined) return;
    loadedByLang[lang] = null; // mark as in-flight so we don't re-request
    loader(lang)
      .then((data) => {
        loadedByLang[lang] = data;
        notifyTranslationsLoaded();
      })
      .catch(() => {
        delete loadedByLang[lang]; // allow retry on next render
      });
  };

  if (typeof window !== 'undefined') {
    ensureLoaded(getLanguage().replace('-', '_'));
  }

  return (text, vars = null) => {
    const lang = getLanguage().replace('-', '_');
    ensureLoaded(lang);
    const data = loadedByLang[lang] || loadedByLang['en'] || {};
    return t(data, text, vars);
  };
};

// Compat shim for legacy `import lang from '@/utils/Language'` usage.
// Provides the old class-style API so feature components (allHome, lahip, etc.)
// don't need to be migrated all at once.
export default {
  getCurrentLanguage: getLanguage,
  getLanguageString,
  t
};
